import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { App } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

/**
 * Live updates for the Play Store / App Store app.
 *
 * The store app ships with a copy of the web build. Every web deploy also
 * publishes that build as a zip plus live-update/manifest.json
 * (scripts/package-live-update.js); the app reads the manifest, downloads a
 * newer bundle in the background and switches to it at a moment that cannot
 * interrupt an exercise: on the next app start, or when the patient comes back
 * after a long break. The previous bundle stays until the new one has rendered
 * once — if it never does, the updater rolls back by itself.
 *
 * Updates only move forward: a bundle is skipped unless its commit is newer
 * than the running one (so a store build from a newer commit, e.g. a test
 * build from a branch, is never replaced by an older main), when it needs a
 * newer native app (manifest minNativeVersion, set in package.json "mobile"),
 * or when it failed on this device before. Rolling back means reverting on
 * main, which produces a newer commit.
 *
 * craco.config.js stamps every build with its commit; set
 * REACT_APP_LIVE_UPDATE_URL=off to build an app that never updates itself.
 */

export const MANIFEST_URL =
  process.env.REACT_APP_LIVE_UPDATE_URL === "off"
    ? null
    : process.env.REACT_APP_LIVE_UPDATE_URL || "https://portal.sophyapp.com/live-update/manifest.json";
const OWN_COMMIT = process.env.REACT_APP_COMMIT_SHA;
const OWN_COMMIT_TIME = Number(process.env.REACT_APP_COMMIT_TIME) || 0;

const PENDING_KEY = "liveUpdate.pendingBundleId";
const CHECK_INTERVAL_MS = 10 * 60 * 1000;
// Away longer than this, the patient is not in the middle of anything.
const APPLY_AFTER_BACKGROUND_MS = 30 * 60 * 1000;

let lastCheckAt = 0;
let checking = null;
let backgroundSince = null;

export function isLiveUpdateEnabled() {
  return Capacitor.isNativePlatform() && Boolean(MANIFEST_URL && OWN_COMMIT);
}

/** Compares dotted version strings numerically: -1, 0 or 1. */
export function compareVersions(a, b) {
  const left = String(a).split(".").map((part) => parseInt(part, 10) || 0);
  const right = String(b).split(".").map((part) => parseInt(part, 10) || 0);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const difference = (left[i] || 0) - (right[i] || 0);
    if (difference !== 0) return difference > 0 ? 1 : -1;
  }
  return 0;
}

/**
 * Why a manifest's bundle should not be installed here, or null if it should.
 * The zip must come from the manifest's own origin, so a tampered manifest
 * cannot point the app somewhere else.
 */
export function rejectReason(manifest, { ownCommit, ownCommitTime, nativeVersion, manifestUrl }) {
  if (!manifest || !manifest.version || !manifest.url || !manifest.commit) return "invalid manifest";
  if (!/^[0-9a-f]{64}$/i.test(manifest.checksum || "")) return "invalid checksum";
  if (manifest.commit === ownCommit) return "already running";
  if (!(Number(manifest.commitTime) > ownCommitTime)) return "not newer";
  if (manifest.minNativeVersion && compareVersions(nativeVersion, manifest.minNativeVersion) < 0) {
    return `needs native ${manifest.minNativeVersion}`;
  }
  const bundleUrl = new URL(manifest.url, manifestUrl);
  if (bundleUrl.origin !== new URL(manifestUrl).origin) return "foreign bundle url";
  return null;
}

function readPending() {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch (e) {
    return null;
  }
}

function writePending(id) {
  try {
    if (id) localStorage.setItem(PENDING_KEY, id);
    else localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    // Private storage unavailable: the update then waits for the next check.
  }
}

async function fetchManifest() {
  // Native HTTP: the WebView's own fetch would need CORS headers on the web server.
  const response = await CapacitorHttp.get({
    url: `${MANIFEST_URL}?t=${Date.now()}`,
    headers: { "Cache-Control": "no-cache" },
    responseType: "json",
  });
  if (response.status !== 200) throw new Error(`manifest HTTP ${response.status}`);
  return typeof response.data === "string" ? JSON.parse(response.data) : response.data;
}

/**
 * Switches to a bundle downloaded earlier, if there is one, and resolves to
 * whether it did. In the app the page reloads from the new bundle right away,
 * so code after a successful switch never runs.
 */
export async function applyPendingUpdate() {
  const pendingId = readPending();
  if (!pendingId) return false;
  // Cleared first: should the bundle fail, the next start does not try it again.
  writePending(null);

  const [{ bundles }, { bundle: current }] = await Promise.all([
    CapacitorUpdater.list(),
    CapacitorUpdater.current(),
  ]);
  const pending = bundles.find((bundle) => bundle.id === pendingId);
  if (!pending || pending.status !== "success" || pending.id === current.id) return false;

  await CapacitorUpdater.set({ id: pending.id });
  return true;
}

/** Downloads the manifest's bundle when it is new and suitable; it is applied later. */
export async function checkForUpdate({ force = false } = {}) {
  if (!isLiveUpdateEnabled()) return null;
  if (checking) return checking;
  if (!force && Date.now() - lastCheckAt < CHECK_INTERVAL_MS) return null;
  lastCheckAt = Date.now();

  checking = (async () => {
    const manifest = await fetchManifest();
    const { native } = await CapacitorUpdater.current();
    const reason = rejectReason(manifest, {
      ownCommit: OWN_COMMIT,
      ownCommitTime: OWN_COMMIT_TIME,
      nativeVersion: native,
      manifestUrl: MANIFEST_URL,
    });
    if (reason) return null;

    const { bundles } = await CapacitorUpdater.list();
    const known = bundles.find((bundle) => bundle.version === manifest.version);
    // A version that already failed here stays skipped until the next deploy.
    if (known && known.status === "error") return null;

    const bundle =
      known && known.status === "success"
        ? known
        : await CapacitorUpdater.download({
            url: new URL(manifest.url, MANIFEST_URL).href,
            version: manifest.version,
            checksum: manifest.checksum,
          });

    const previous = readPending();
    if (previous && previous !== bundle.id) {
      await CapacitorUpdater.delete({ id: previous }).catch(() => {});
    }
    writePending(bundle.id);
    return bundle;
  })()
    .catch((error) => {
      console.warn("Live update check failed:", error);
      return null;
    })
    .finally(() => {
      checking = null;
    });

  return checking;
}

async function onAppStateChange({ isActive }) {
  if (!isActive) {
    backgroundSince = Date.now();
    return;
  }
  const away = backgroundSince ? Date.now() - backgroundSince : 0;
  backgroundSince = null;
  try {
    if (away >= APPLY_AFTER_BACKGROUND_MS && (await applyPendingUpdate())) return;
  } catch (error) {
    console.warn("Live update could not be applied:", error);
  }
  checkForUpdate();
}

/**
 * Called once the app has rendered. In the browser it does nothing; in the
 * store app it confirms that this bundle works, switches to a bundle that was
 * downloaded during the last session, hides the splash screen and starts
 * looking for the next update.
 */
export async function startLiveUpdates() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
    if (isLiveUpdateEnabled() && (await applyPendingUpdate())) return;
  } catch (error) {
    console.warn("Live update could not be applied:", error);
  }
  SplashScreen.hide().catch(() => {});

  if (!isLiveUpdateEnabled()) return;
  App.addListener("appStateChange", onAppStateChange);
  checkForUpdate({ force: true });
}
